'use client';

import { Icon } from '@iconify/react';
import { Button } from '@heroui/react';
import React, { useState, useEffect, useCallback } from 'react';
import { useNode } from '@craftjs/core';
import useClientStore from '@/store/useClientStore';
import { Dealership as DealershipRow } from '@/utils/types';
import { supabase } from '@/lib/supabase';
import { useTranslation } from '@/i18n/hooks/useTranslation';
import OpeningHours from './OpeningHours';
import { getDealershipHours, type PublicHoursByDealership } from '@/lib/dealership-hours';
import { sucursalesDelCliente, horasDelCliente } from '@/lib/sucursales-del-store';
import { DEALERSHIP_PUBLIC_COLS } from '@/lib/website-config-cols';
import DealershipMapPanel from './DealershipMapPanel';
import { AreaDePiezas as AreaDePiezasCraft, Pieza as PiezaCraft } from '@/components/builder2/utils/piezaLibre';

/**
 * Esta seccion se monta en dos lugares: dentro del `<Editor>` de Craft (el
 * builder y el sitio publicado) y fuera de el (el home tradicional, que es lo
 * que se sirve cuando `is_enabled` es false, y `/embed`). `AreaDePiezas` y
 * `Pieza` llaman a `useNode`: sin `<Editor>` arriba eso lanza el invariant de
 * Craft y se lleva puesta la pagina entera. Fuera del editor van estas
 * versiones planas, que dejan el mismo DOM sin tocar nodos de Craft.
 */
const AreaPlana: React.FC<React.ComponentProps<typeof AreaDePiezasCraft>> = ({
  className,
  style,
  children,
}) => (
  <div className={className} style={style}>
    {children}
  </div>
);

const PiezaPlana: React.FC<React.ComponentProps<typeof PiezaCraft>> = ({ children }) => (
  <>{children}</>
);

/** Extiende Dealership con `phones` y `emails` (columnas jsonb = arrays de
 *  strings) sin tocar el tipo base */
type Dealership = DealershipRow & {
  phones?: string[] | null;
  emails?: string[] | null;
};

interface HowToArriveProps {
  title?: string;
  subtitle?: string;
  titleAlignment?: 'left' | 'center' | 'right';
  height?: string;
  buttonLabel?: string;
  /** Color de acento del builder: pinta los íconos de la tarjeta y el botón.
   *  Ausente = el `text-primary` de siempre, que es lo que ven todas las
   *  páginas publicadas hasta hoy y el home, que no manda ninguna prop. */
  accentColor?: string;
  bgColor?: string;
  textColor?: string;
  subtitleColor?: string;
  cardBgColor?: string;
  cardBorderColor?: string;
  labelColor?: string;
  valueColor?: string;
}

export default function HowToArrive({
  title,
  subtitle,
  titleAlignment = 'center',
  height = '400px',
  buttonLabel,
  accentColor,
  bgColor,
  textColor,
  subtitleColor,
  cardBgColor,
  cardBorderColor,
  labelColor,
  valueColor,
}: HowToArriveProps) {
  let connectors: any = null;
  let selected = false;

  try {
    const nodeData = useNode((state) => ({ selected: state.events.selected }));
    connectors = nodeData.connectors;
    selected = nodeData.selected;
  } catch {
    connectors = null;
    selected = false;
  }

  // `connectors` queda en null exactamente cuando el `useNode` de arriba tiro,
  // o sea cuando no hay `<Editor>`: la misma condicion que haria explotar a
  // `AreaDePiezas` y `Pieza`. Sirve entonces para elegir la version plana sin
  // agregar ninguna deteccion nueva.
  const AreaDePiezas = connectors ? AreaDePiezasCraft : AreaPlana;
  const Pieza = connectors ? PiezaCraft : PiezaPlana;

  const { client } = useClientStore();
  const { t } = useTranslation();

  // Los íconos de la tarjeta y el botón siguen el acento que eligió el dueño en
  // el builder. Sin acento no se toca nada: queda el `text-primary` de siempre,
  // que es lo que ve el home y toda página publicada antes de este arreglo.
  const iconoAcento = (extra = '') => ({
    className: `text-xl flex-shrink-0${extra}${accentColor ? '' : ' text-primary'}`,
    style: accentColor ? { color: accentColor } : undefined,
  });

  const [dealerships, setDealerships] = useState<Dealership[]>([]);
  const [selectedDealership, setSelectedDealership] = useState<Dealership | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Ticket #59: el horario sale de Agenda (dealership_hours) por RPC, no del
  // JSONB legacy `dealerships.opening_hours`.
  const [hoursByDealership, setHoursByDealership] = useState<PublicHoursByDealership>({});

  useEffect(() => {
    const fetchDealerships = async () => {
      if (client?.id) {
        // Las sucursales ya vienen embebidas en la fila del tenant; solo si el
        // store no las trae se consultan como antes.
        let data = sucursalesDelCliente<Dealership>(client);
        if (!data) {
          ({ data } = await supabase
            .from('dealerships')
            .select<string, Dealership>(DEALERSHIP_PUBLIC_COLS)
            .eq('client_id', client.id));
        }

        // Filter out dealerships with invalid/missing location
        const valid = (data || []).filter(
          (d: any) => d.location && d.location.lat != null && d.location.lng != null
        );
        if (valid.length > 0) {
          setDealerships(valid);
          setSelectedDealership(valid[0]);
        } else if (client.location?.lat != null && client.location?.lng != null) {
          const defaultDealership: Dealership = {
            id: 'default',
            client_id: client.id,
            address: client.contact?.address || '',
            phone: client.contact?.phone || '',
            email: client.contact?.email || '',
            location: {
              lat: Number(client.location.lat),
              lng: Number(client.location.lng),
            },
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          };
          setDealerships([defaultDealership]);
          setSelectedDealership(defaultDealership);
        }
      }
      setIsLoading(false);
    };

    fetchDealerships();
  }, [client]);

  // Horario de todas las sucursales del tenant, en una sola llamada. Va aparte
  // del fetch de sucursales porque sale de otra fuente (Agenda) y por RPC: si
  // falla, la tarjeta de contacto se pinta igual, sin el bloque de horario.
  useEffect(() => {
    if (!client?.id) {
      setHoursByDealership({});
      return;
    }
    // El layout ya resolvió el horario de todas las sucursales y lo dejó en el
    // store; la RPC queda para cuando no esté.
    const enStore = horasDelCliente(client);
    if (enStore) {
      setHoursByDealership(enStore);
      return;
    }
    let cancelado = false;
    getDealershipHours(client.id).then((h) => {
      if (!cancelado) setHoursByDealership(h);
    });
    return () => {
      cancelado = true;
    };
  }, [client?.id]);

  const handleOpenDirections = useCallback((dealership: Dealership) => {
    const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${dealership.location.lat},${dealership.location.lng}`;
    window.open(googleMapsUrl, '_blank');
  }, []);

  const currentIndex = dealerships.findIndex((d) => d.id === selectedDealership?.id);

  const handleNext = () => {
    if (dealerships.length <= 1) return;
    const nextIndex = (currentIndex + 1) % dealerships.length;
    setSelectedDealership(dealerships[nextIndex]);
  };

  const handlePrev = () => {
    if (dealerships.length <= 1) return;
    const prevIndex = (currentIndex - 1 + dealerships.length) % dealerships.length;
    setSelectedDealership(dealerships[prevIndex]);
  };

  // Sólo se espera a los datos de Supabase. El estado del mapa vive dentro de
  // DealershipMapPanel a propósito: cuando dependía de él, un fallo del
  // proveedor dejaba la sección entera en un spinner infinito y el visitante
  // perdía dirección, teléfono, email y horarios — datos que no necesitan mapa.
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-primary" />
      </div>
    );
  }

  return (
    <div
      ref={connectors?.connect || null}
      className={!bgColor ? `py-12 bg-slate-50/50 dark:bg-dark-bg ${selected ? 'ring-2 ring-dashed ring-slate-400' : ''}` : `py-12 ${selected ? 'ring-2 ring-dashed ring-slate-400' : ''}`}
      style={bgColor ? { backgroundColor: bgColor } : undefined}
    >
      <AreaDePiezas className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className={`mb-8 ${titleAlignment === 'center' ? 'text-center' : titleAlignment === 'right' ? 'text-right' : 'text-left'}`}>
          <Pieza clave="titulo" tipo="texto" etiqueta="Título">
            <h2
              className={!textColor ? 'text-3xl font-bold text-gray-900 dark:text-white mb-2' : 'text-3xl font-bold mb-2'}
              style={textColor ? { color: textColor } : undefined}
            >
              {title || t('home.howToArrive.title')}
            </h2>
          </Pieza>
          <Pieza clave="subtitulo" tipo="texto" etiqueta="Descripción">
            <p
              className={!subtitleColor ? 'text-gray-600 dark:text-gray-400' : ''}
              style={subtitleColor ? { color: subtitleColor } : undefined}
            >
              {subtitle || t('home.howToArrive.subtitle')}
            </p>
          </Pieza>
        </div>

        <Pieza clave="mapa" tipo="coleccion" etiqueta="Mapa y datos">
          {/* Grid Layout */}
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Info Card - Fixed height */}
            <div className="lg:col-span-1">
              <div
                className={!cardBgColor ? 'bg-white dark:bg-dark-card rounded-xl shadow-sm border border-slate-200/60 dark:border-dark-border h-full min-h-[300px] flex flex-col' : 'rounded-xl shadow-sm h-full min-h-[300px] flex flex-col'}
                style={cardBgColor ? { backgroundColor: cardBgColor, border: `1px solid ${cardBorderColor || 'rgba(0,0,0,0.1)'}` } : undefined}
              >
                {/* Navigation (only if multiple) */}
                {dealerships.length > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-dark-border">
                    <button
                      onClick={handlePrev}
                      className="p-1 rounded-full hover:bg-slate-100 dark:hover:bg-dark-card transition-colors"
                    >
                      <Icon icon="mdi:chevron-left" className="text-xl text-gray-500" />
                    </button>
                    <span className="text-sm text-gray-500">
                      {currentIndex + 1} {t('home.howToArrive.ofTotal')} {dealerships.length}
                    </span>
                    <button
                      onClick={handleNext}
                      className="p-1 rounded-full hover:bg-slate-100 dark:hover:bg-dark-card transition-colors"
                    >
                      <Icon icon="mdi:chevron-right" className="text-xl text-gray-500" />
                    </button>
                  </div>
                )}

                {/* Content */}
                {selectedDealership && (
                  <div className="p-5 flex-1 flex flex-col">
                    <div className="space-y-4 flex-1">
                      {/* Address */}
                      <div className="flex gap-3">
                        <Icon icon="mdi:map-marker" {...iconoAcento(' mt-0.5')} />
                        <div className="min-w-0">
                          <p className={!labelColor ? 'text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1' : 'text-xs uppercase tracking-wide mb-1'} style={labelColor ? { color: labelColor } : undefined}>
                            {t('home.howToArrive.address')}
                          </p>
                          <p className={!valueColor ? 'text-gray-900 dark:text-white text-sm leading-relaxed' : 'text-sm leading-relaxed'} style={valueColor ? { color: valueColor } : undefined}>
                            {selectedDealership.address}
                          </p>
                        </div>
                      </div>

                      {/* Phone */}
                      {(() => {
                        const phoneList = (
                          Array.isArray(selectedDealership.phones) && selectedDealership.phones.length
                            ? selectedDealership.phones
                            : selectedDealership.phone
                              ? [selectedDealership.phone]
                              : []
                        ).map((p) => String(p).trim()).filter(Boolean);

                        return phoneList.length > 0 && (
                          <div className="flex gap-3">
                            <Icon icon="mdi:phone" {...iconoAcento()} />
                            <div>
                              <p className={!labelColor ? 'text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1' : 'text-xs uppercase tracking-wide mb-1'} style={labelColor ? { color: labelColor } : undefined}>
                                {t('home.howToArrive.phone')}
                              </p>
                              <div className="flex flex-col gap-0.5">
                                {phoneList.map((p, i) => (
                                  <a key={i} href={`tel:${p}`} className={!valueColor ? 'text-gray-900 dark:text-white text-sm hover:text-primary transition-colors' : 'text-sm transition-colors'} style={valueColor ? { color: valueColor } : undefined}>
                                    {p}
                                  </a>
                                ))}
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Email(s) — ticket #58: una sucursal puede tener
                          varios correos (columna `emails`), con fallback al
                          `email` único. Cada uno lleva su propio mailto:. */}
                      {(() => {
                        const emailList = (
                          Array.isArray(selectedDealership.emails) && selectedDealership.emails.length
                            ? selectedDealership.emails
                            : selectedDealership.email
                              ? [selectedDealership.email]
                              : []
                        ).map((e) => String(e).trim()).filter(Boolean);

                        return emailList.length > 0 && (
                          <div className="flex gap-3">
                            <Icon icon="mdi:email" {...iconoAcento()} />
                            <div className="min-w-0">
                              <p className={!labelColor ? 'text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1' : 'text-xs uppercase tracking-wide mb-1'} style={labelColor ? { color: labelColor } : undefined}>
                                {t('home.howToArrive.email')}
                              </p>
                              <div className="flex flex-col gap-0.5">
                                {emailList.map((e, i) => (
                                  <a key={i} href={`mailto:${e}`} className={!valueColor ? 'text-gray-900 dark:text-white text-sm hover:text-primary transition-colors truncate block' : 'text-sm transition-colors truncate block'} style={valueColor ? { color: valueColor } : undefined}>
                                    {e}
                                  </a>
                                ))}
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Horario de atención */}
                      {hoursByDealership[Number(selectedDealership.id)]?.length ? (
                        <div className="flex gap-3">
                          <Icon icon="mdi:clock-outline" {...iconoAcento(' mt-0.5')} />
                          <div className="min-w-0 flex-1">
                            <p className={!labelColor ? 'text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1' : 'text-xs uppercase tracking-wide mb-1'} style={labelColor ? { color: labelColor } : undefined}>
                              {t('home.howToArrive.hours')}
                            </p>
                            <OpeningHours
                              hours={hoursByDealership[Number(selectedDealership.id)]}
                              labelColor={labelColor}
                              valueColor={valueColor}
                            />
                          </div>
                        </div>
                      ) : null}
                    </div>

                    {/* Button */}
                    <Button
                      color="primary"
                      className="w-full mt-4 text-white"
                      style={accentColor ? { backgroundColor: accentColor } : undefined}
                      onPress={() => handleOpenDirections(selectedDealership)}
                    >
                      {buttonLabel || t('home.howToArrive.button')}
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Map */}
            <div className="lg:col-span-2">
              <DealershipMapPanel
                dealerships={dealerships}
                selectedDealership={selectedDealership}
                onSelect={setSelectedDealership}
                onOpenDirections={handleOpenDirections}
                height={height}
                clientId={client?.id}
              />
            </div>
          </div>
        </Pieza>
      </AreaDePiezas>
    </div>
  );
}

// Configuración de CraftJS
HowToArrive.craft = {
  displayName: 'HowToArrive',
  props: {
    title: '',
    subtitle: '',
    titleAlignment: 'center',
    height: '400px',
    buttonLabel: '',
  },
  rules: {
    canDrag: () => true,
    canDrop: () => true,
    canMoveIn: () => true,
  },
};
